/**
 * Centralized SEO configuration for SRG Consulting PLLC.
 *
 * Single source of truth mapping every route to its title, description,
 * canonical URL, robots directive, and optional og:image override.
 * The <RouteSEO /> component reads from this map and applies tags via
 * useSEO, eliminating the need for per-page useSEO calls.
 *
 * Routes NOT listed here fall back to the homepage defaults (handled by
 * RouteSEO). Redirect-only routes (Navigate components) are intentionally
 * omitted — they never render metadata.
 */

const SITE = "https://srgconsultingpllc.com";
const DEFAULT_OG =
  "https://vibe.filesafe.space/meta/1787785978701289352/og_image.png";

export interface SEOEntry {
  title: string;
  description: string;
  canonical: string;
  robots?: string;
  ogImage?: string;
  ogType?: string;
}

const index = (p: string) => `${SITE}${p}`;

export const seoConfig: Record<string, SEOEntry> = {
  "/": {
    title: "Business Owner Strategy & Succession Planning | SRG Consulting",
    description:
      "SRG Consulting PLLC helps business owners connect succession, buy-sell funding, key-person protection, executive benefits, trust coordination, and wealth preservation into one coordinated strategic roadmap.",
    canonical: index("/"),
    ogImage: DEFAULT_OG,
  },

  // Canonical national service URLs
  "/business-owner-financial-strategy": {
    title: "Business Owner Financial Strategy | SRG Consulting",
    description:
      "A coordinated financial roadmap connecting business growth, continuity, capital, retirement, and exit strategy for closely held business owners.",
    canonical: index("/business-owner-financial-strategy"),
    ogImage: DEFAULT_OG,
  },
  "/business-succession-planning": {
    title: "Business Succession Planning | SRG Consulting",
    description:
      "Ownership transition strategy for closely held businesses — timing, valuation coordination, successor readiness, and funded continuity.",
    canonical: index("/business-succession-planning"),
    ogImage: DEFAULT_OG,
  },
  "/buy-sell-agreement-funding": {
    title: "Buy-Sell Agreement Funding | SRG Consulting",
    description:
      "Structuring and funding buy-sell agreements so ownership transfers are executable the moment a death, disability, or departure trigger occurs.",
    canonical: index("/buy-sell-agreement-funding"),
    ogImage: DEFAULT_OG,
  },
  "/key-person-risk-protection": {
    title: "Key Person Risk Protection | SRG Consulting",
    description:
      "Identify concentration risk in leadership and structure key-person coverage to protect enterprise continuity and revenue.",
    canonical: index("/key-person-risk-protection"),
    ogImage: DEFAULT_OG,
  },
  "/executive-retention-planning": {
    title: "Executive Retention Planning | SRG Consulting",
    description:
      "Customized executive compensation and retention strategies — bonus plans, split-dollar, deferred compensation, and SERPs for privately held companies.",
    canonical: index("/executive-retention-planning"),
    ogImage: DEFAULT_OG,
  },
  "/business-owner-trust-coordination": {
    title: "Business Owner Trust Coordination | SRG Consulting",
    description:
      "Trust and estate planning coordination for business owners — coordinating trusts, entity structures, and buy-sell agreements with your attorney and CPA.",
    canonical: index("/business-owner-trust-coordination"),
    ogImage: DEFAULT_OG,
  },
  "/business-owner-retirement-planning": {
    title: "Business Owner Retirement Planning | SRG Consulting",
    description:
      "Retirement income strategy and rollover education for business owners and executives preparing to transition out of the company.",
    canonical: index("/business-owner-retirement-planning"),
    ogImage: DEFAULT_OG,
  },
  "/commercial-capital-solutions": {
    title: "Commercial Capital Solutions | SRG Consulting",
    description:
      "Commercial financing strategy and lender introductions for growth, acquisition, equipment, and working capital needs.",
    canonical: index("/commercial-capital-solutions"),
    ogImage: DEFAULT_OG,
  },
  "/401k-rollover-education": {
    title: "401(k) Rollover Education | SRG Consulting",
    description:
      "Educational guidance on 401(k) rollovers, IRA options, and Roth conversions for business owners and executives changing roles or retiring.",
    canonical: index("/401k-rollover-education"),
    ogImage: DEFAULT_OG,
  },
  "/wealth-preservation-strategy": {
    title: "Wealth Preservation Strategy | SRG Consulting",
    description:
      "Multi-generational transfer planning coordinated with estate counsel and CPA, including trust-based freeze structures and estate liquidity.",
    canonical: index("/wealth-preservation-strategy"),
    ogImage: DEFAULT_OG,
  },
  "/where-srg-fits": {
    title: "Where SRG Fits | SRG Consulting",
    description:
      "How SRG Consulting coordinates the business and financial strategy around your CPA and attorney — the roadmap connecting every component of your plan.",
    canonical: index("/where-srg-fits"),
    ogImage: DEFAULT_OG,
  },
  "/business-exit-planning": {
    title: "Business Exit Planning | SRG Consulting",
    description:
      "Prepare your business for sale or transition — valuation readiness, tax-aware structuring, and a documented exit timeline.",
    canonical: index("/business-exit-planning"),
    ogImage: DEFAULT_OG,
  },

  // Core pages
  "/start-here": {
    title: "Start Here — Guided Path for Business Owners | SRG Consulting",
    description:
      "Not sure where to begin? Take the guided path through SRG Consulting's education-first resources and find the area that matters most to your situation.",
    canonical: index("/start-here"),
    ogImage: DEFAULT_OG,
  },
  "/questionnaire": {
    title: "Business Owner Exposure Score™ | SRG Consulting PLLC",
    description:
      "Take SRG Consulting's 12-question Business Owner Exposure Score™ to identify gaps in your business protection, tax exposure, and succession plan.",
    canonical: index("/questionnaire"),
    ogImage: DEFAULT_OG,
  },
  "/case-studies": {
    title: "Case Studies & Planning Scenarios | SRG Consulting",
    description:
      "Illustrative planning scenarios showing how SRG Consulting coordinates succession, buy-sell funding, executive retention, and wealth preservation.",
    canonical: index("/case-studies"),
    ogImage: DEFAULT_OG,
  },
  "/resources": {
    title: "Resource Center | SRG Consulting",
    description:
      "Guides, calculators, matrices, and downloadable resources for business owners navigating succession, capital, and wealth preservation.",
    canonical: index("/resources"),
    ogImage: DEFAULT_OG,
  },
  "/portal": {
    title: "Executive Command Center | SRG Consulting",
    description:
      "Private access portal with interactive tools, the Exit Strategy dashboard, trust planning guides, and downloadable resources for assessment-takers.",
    canonical: index("/portal"),
    ogImage: DEFAULT_OG,
  },
  "/arizona-regulatory-landscape": {
    title:
      "Arizona Regulatory Landscape for Financial Consulting | SRG Consulting",
    description:
      "An educational overview of the Arizona regulatory landscape — AZCC, DIFI, fiduciary duties, and how SRG Consulting operates within it.",
    canonical: index("/arizona-regulatory-landscape"),
    ogImage: DEFAULT_OG,
  },
  "/terms": {
    title: "Terms, Disclosures & Code of Ethics | SRG Consulting",
    description:
      "Terms of use, compensation disclosure, scope of service, and the SRG Consulting code of ethics.",
    canonical: index("/terms"),
    ogImage: DEFAULT_OG,
  },
  "/privacy": {
    title: "Privacy Policy | SRG Consulting",
    description:
      "How SRG Consulting PLLC collects, uses, and protects your information.",
    canonical: index("/privacy"),
    ogImage: DEFAULT_OG,
  },
  "/pricing": {
    title: "How We Work | Flat-Fee Engagements | SRG Consulting",
    description:
      "SRG Consulting works on flat-fee engagements scoped to the work — quarterly and annual corporate reviews, no subscriptions or rate cards.",
    canonical: index("/pricing"),
    ogImage: DEFAULT_OG,
  },
  "/faq": {
    title: "Frequently Asked Questions | SRG Consulting",
    description:
      "Answers to common questions about business succession, buy-sell funding, trust coordination, and how SRG Consulting works with your CPA and attorney.",
    canonical: index("/faq"),
    ogImage: DEFAULT_OG,
  },
  "/glossary": {
    title: "Financial & Estate Planning Glossary | SRG Consulting",
    description:
      "Plain-language definitions of business succession, buy-sell, trust, and wealth preservation terms used throughout the SRG Consulting site.",
    canonical: index("/glossary"),
    ogImage: DEFAULT_OG,
  },
  "/long-term-care": {
    title: "Long-Term Care Planning | SRG Consulting",
    description:
      "Long-term care funding strategies, cost estimates, and Medicaid basics for business owners and families.",
    canonical: index("/long-term-care"),
    ogImage: DEFAULT_OG,
  },
  "/insights": {
    title: "Insights & Articles | SRG Consulting",
    description:
      "Educational articles on business succession, buy-sell funding, commercial lending, key-person risk, and wealth preservation for business owners.",
    canonical: index("/insights"),
    ogImage: DEFAULT_OG,
  },
  "/real-estate-strategy": {
    title: "Real Estate Investor Strategy | SRG Consulting",
    description:
      "Coordination of real estate holdings with succession, capital, and wealth preservation strategy for investor-owners.",
    canonical: index("/real-estate-strategy"),
    ogImage: DEFAULT_OG,
  },
  "/wealth-preservation-scottsdale": {
    title: "Wealth Preservation in Scottsdale | SRG Consulting",
    description:
      "Wealth preservation and succession strategy for Scottsdale business owners and families.",
    canonical: index("/wealth-preservation-scottsdale"),
    ogImage: DEFAULT_OG,
  },
  "/trust-estate-education": {
    title: "Trust & Estate Education for Business Owners | SRG Consulting",
    description:
      "Educational overview of trust structures — ILIT, BDIT, GRAT, dynasty, QTIP, Crummey — and how they coordinate with business succession.",
    canonical: index("/trust-estate-education"),
    ogImage: DEFAULT_OG,
  },
  "/business-strategy-phoenix": {
    title: "Business Strategy for Phoenix Owners | SRG Consulting",
    description:
      "Business and financial strategy for Phoenix-area business owners — succession, capital, and wealth coordination.",
    canonical: index("/business-strategy-phoenix"),
    ogImage: DEFAULT_OG,
  },
  "/retirement-planning-arizona": {
    title: "Retirement Planning in Arizona | SRG Consulting",
    description:
      "Retirement income and rollover education for Arizona business owners and executives.",
    canonical: index("/retirement-planning-arizona"),
    ogImage: DEFAULT_OG,
  },
  "/mesa-commercial-funding": {
    title: "Commercial Funding in Mesa | SRG Consulting",
    description:
      "Commercial financing strategy and lender introductions for Mesa-area business owners.",
    canonical: index("/mesa-commercial-funding"),
    ogImage: DEFAULT_OG,
  },
  "/chandler-business-succession": {
    title: "Business Succession in Chandler | SRG Consulting",
    description:
      "Succession and continuity planning for Chandler business owners and partners.",
    canonical: index("/chandler-business-succession"),
    ogImage: DEFAULT_OG,
  },
  "/glendale-business-continuity": {
    title: "Business Continuity in Glendale | SRG Consulting",
    description:
      "Business continuity and owner-dependency risk planning for Glendale business owners.",
    canonical: index("/glendale-business-continuity"),
    ogImage: DEFAULT_OG,
  },
  "/tempe-executive-benefits": {
    title: "Executive Benefits in Tempe | SRG Consulting",
    description:
      "Executive retention and compensation strategies for Tempe-area privately held companies.",
    canonical: index("/tempe-executive-benefits"),
    ogImage: DEFAULT_OG,
  },
  "/peoria-risk-assessment": {
    title: "Risk Assessment for Peoria Business Owners | SRG Consulting",
    description:
      "Business owner exposure and continuity risk assessment for Peoria companies.",
    canonical: index("/peoria-risk-assessment"),
    ogImage: DEFAULT_OG,
  },
  "/gilbert-commercial-lending": {
    title: "Commercial Lending in Gilbert | SRG Consulting",
    description:
      "Commercial lending strategy and lender introductions for Gilbert business owners.",
    canonical: index("/gilbert-commercial-lending"),
    ogImage: DEFAULT_OG,
  },
  "/strategic-partnerships": {
    title: "Strategic Partnerships & Authority Building | SRG Consulting",
    description:
      "How SRG Consulting builds authority through CPA and attorney partner articles, co-authored guides, chambers of commerce, and industry publications.",
    canonical: index("/strategic-partnerships"),
    ogImage: DEFAULT_OG,
  },
  "/about-anthony-damato": {
    title: "About Anthony D'Amato | SRG Consulting",
    description:
      "Anthony D'Amato, Principal of SRG Consulting PLLC, brings more than three decades of experience across commercial finance, mortgage banking, real estate, and trust-centered wealth strategy.",
    canonical: index("/about-anthony-damato"),
    ogImage: DEFAULT_OG,
  },
  "/founders-message": {
    title: "Founder's Message | SRG Consulting",
    description:
      "A message from Anthony D'Amato on why SRG Consulting was built as an education-first financial resource for business owners.",
    canonical: index("/founders-message"),
    ogImage: DEFAULT_OG,
  },
  "/reviews": {
    title: "Client Reviews & Experiences | SRG Consulting",
    description:
      "Client reviews and experiences with SRG Consulting PLLC's business succession and wealth preservation services.",
    canonical: index("/reviews"),
    ogImage: DEFAULT_OG,
  },
  "/news-updates": {
    title: "News & Updates | SRG Consulting",
    description:
      "Latest news, regulatory updates, and announcements from SRG Consulting PLLC.",
    canonical: index("/news-updates"),
    ogImage: DEFAULT_OG,
  },
  "/provision-glossary": {
    title: "Trust Provision Glossary | SRG Consulting",
    description:
      "Searchable, filterable glossary of common trust provisions — spendthrift, no-contest, discretionary distribution, and trustee removal clauses.",
    canonical: index("/provision-glossary"),
    ogImage: DEFAULT_OG,
  },
  "/trust-comparison": {
    title: "Compare Trust Structures Side-by-Side | SRG Consulting",
    description:
      "Select two trust structures and compare provisions, tax treatment, and succession mechanics at a glance.",
    canonical: index("/trust-comparison"),
    ogImage: DEFAULT_OG,
  },
  "/find-your-trust": {
    title: "Find Your Trust — Quiz for Business Owners | SRG Consulting",
    description:
      "Answer a few questions about your goals and get a recommended trust structure with applicable provisions.",
    canonical: index("/find-your-trust"),
    ogImage: DEFAULT_OG,
  },
  "/disclosure-center": {
    title: "Disclosure Center | SRG Consulting",
    description:
      "Credentials, licensing, compensation, scope of service, authorship, and professional-review standards for SRG Consulting PLLC.",
    canonical: index("/disclosure-center"),
    ogImage: DEFAULT_OG,
  },

  // Industries
  "/industries/construction": {
    title: "Construction Business Strategy | SRG Consulting",
    description:
      "Succession, buy-sell funding, key-person protection, and capital strategy for construction companies and contractors.",
    canonical: index("/industries/construction"),
    ogImage: DEFAULT_OG,
  },
  "/industries/manufacturing": {
    title: "Manufacturing Business Strategy | SRG Consulting",
    description:
      "Succession, equipment financing, executive retention, and wealth preservation for manufacturing business owners.",
    canonical: index("/industries/manufacturing"),
    ogImage: DEFAULT_OG,
  },
  "/industries/healthcare": {
    title: "Healthcare Practice Strategy | SRG Consulting",
    description:
      "Succession, executive retention, and wealth preservation for healthcare practice owners.",
    canonical: index("/industries/healthcare"),
    ogImage: DEFAULT_OG,
  },
  "/industries/dental": {
    title: "Dental Practice Strategy | SRG Consulting",
    description:
      "Succession, executive retention, and wealth preservation for dental practice owners.",
    canonical: index("/industries/dental"),
    ogImage: DEFAULT_OG,
  },
  "/industries/logistics": {
    title: "Logistics Business Strategy | SRG Consulting",
    description:
      "Succession, capital, and continuity strategy for logistics and transportation business owners.",
    canonical: index("/industries/logistics"),
    ogImage: DEFAULT_OG,
  },
  "/industries/real-estate-investors": {
    title: "Real Estate Investor Strategy | SRG Consulting",
    description:
      "Coordination of real estate holdings with succession, capital, and wealth preservation for investor-owners.",
    canonical: index("/industries/real-estate-investors"),
    ogImage: DEFAULT_OG,
  },

  // Business continuity
  "/business-continuity/partner-exit-planning": {
    title: "Partner Exit Planning | SRG Consulting",
    description:
      "Plan for a partner's death, disability, divorce, or departure with a funded buy-sell and continuity strategy.",
    canonical: index("/business-continuity/partner-exit-planning"),
    ogImage: DEFAULT_OG,
  },
  "/business-continuity/family-business-continuity": {
    title: "Family Business Continuity | SRG Consulting",
    description:
      "Keep the company in the family — continuity, governance, and succession planning for family-owned businesses.",
    canonical: index("/business-continuity/family-business-continuity"),
    ogImage: DEFAULT_OG,
  },
  "/business-continuity/owner-dependency-risk": {
    title: "Owner Dependency Risk | SRG Consulting",
    description:
      "Identify and reduce owner-dependency risk so the business can survive and transfer without you.",
    canonical: index("/business-continuity/owner-dependency-risk"),
    ogImage: DEFAULT_OG,
  },
  "/business-continuity/valuation-readiness": {
    title: "Valuation Readiness | SRG Consulting",
    description:
      "Prepare your business for an accurate, defensible valuation before a sale, transfer, or triggering event.",
    canonical: index("/business-continuity/valuation-readiness"),
    ogImage: DEFAULT_OG,
  },
  "/business-continuity/arizona-buy-sell-funding": {
    title: "Arizona Buy-Sell Funding | SRG Consulting",
    description:
      "Fund buy-sell agreements for Arizona business owners after the Connelly decision.",
    canonical: index("/business-continuity/arizona-buy-sell-funding"),
    ogImage: DEFAULT_OG,
  },
  "/business-continuity/arizona-key-person-protection": {
    title: "Arizona Key Person Protection | SRG Consulting",
    description:
      "Key-person coverage and continuity planning for Arizona business owners.",
    canonical: index("/business-continuity/arizona-key-person-protection"),
    ogImage: DEFAULT_OG,
  },
  "/business-continuity/arizona-executive-benefits": {
    title: "Arizona Executive Benefits | SRG Consulting",
    description:
      "Executive retention and compensation strategies for Arizona privately held companies.",
    canonical: index("/business-continuity/arizona-executive-benefits"),
    ogImage: DEFAULT_OG,
  },
  "/business-continuity/arizona-business-owner-estate-planning": {
    title: "Arizona Business Owner Estate Planning | SRG Consulting",
    description:
      "Estate planning coordination for Arizona business owners — trusts, entity continuity, and estate liquidity.",
    canonical: index(
      "/business-continuity/arizona-business-owner-estate-planning",
    ),
    ogImage: DEFAULT_OG,
  },
  "/business-continuity/wealth-preservation-business-owners": {
    title: "Wealth Preservation for Business Owners | SRG Consulting",
    description:
      "Move business value to your personal and family balance sheet tax-efficiently.",
    canonical: index(
      "/business-continuity/wealth-preservation-business-owners",
    ),
    ogImage: DEFAULT_OG,
  },

  // Loans
  "/loans/commercial-loans-phoenix": {
    title: "Commercial Loans in Phoenix | SRG Consulting",
    description:
      "Commercial loan strategy and lender introductions for Phoenix-area business owners.",
    canonical: index("/loans/commercial-loans-phoenix"),
    ogImage: DEFAULT_OG,
  },
  "/loans/business-line-of-credit-arizona": {
    title: "Business Line of Credit in Arizona | SRG Consulting",
    description:
      "Business line of credit strategy and lender introductions for Arizona business owners.",
    canonical: index("/loans/business-line-of-credit-arizona"),
    ogImage: DEFAULT_OG,
  },
  "/loans/sba-loans-phoenix-scottsdale": {
    title: "SBA Loans in Phoenix & Scottsdale | SRG Consulting",
    description:
      "SBA loan strategy and lender introductions for Phoenix and Scottsdale business owners.",
    canonical: index("/loans/sba-loans-phoenix-scottsdale"),
    ogImage: DEFAULT_OG,
  },
  "/loans/equipment-financing-contractors": {
    title: "Equipment Financing for Contractors | SRG Consulting",
    description:
      "Equipment financing strategy and lender introductions for contractors and construction companies.",
    canonical: index("/loans/equipment-financing-contractors"),
    ogImage: DEFAULT_OG,
  },
  "/loans/working-capital-small-businesses": {
    title: "Working Capital for Small Businesses | SRG Consulting",
    description:
      "Working capital loan strategy and lender introductions for small businesses.",
    canonical: index("/loans/working-capital-small-businesses"),
    ogImage: DEFAULT_OG,
  },
  "/loans/commercial-real-estate-arizona": {
    title: "Commercial Real Estate Loans in Arizona | SRG Consulting",
    description:
      "Commercial real estate financing strategy and lender introductions for Arizona business owners.",
    canonical: index("/loans/commercial-real-estate-arizona"),
    ogImage: DEFAULT_OG,
  },
  "/loans/business-acquisition-financing": {
    title: "Business Acquisition Financing | SRG Consulting",
    description:
      "Acquisition financing strategy and lender introductions for buyers of closely held businesses.",
    canonical: index("/loans/business-acquisition-financing"),
    ogImage: DEFAULT_OG,
  },
  "/loans/medical-practice-financing": {
    title: "Medical Practice Financing | SRG Consulting",
    description:
      "Financing strategy and lender introductions for medical practice owners.",
    canonical: index("/loans/medical-practice-financing"),
    ogImage: DEFAULT_OG,
  },
  "/loans/construction-company-financing": {
    title: "Construction Company Financing | SRG Consulting",
    description:
      "Financing strategy and lender introductions for construction companies and contractors.",
    canonical: index("/loans/construction-company-financing"),
    ogImage: DEFAULT_OG,
  },
  "/loans/manufacturing-equipment-financing": {
    title: "Manufacturing Equipment Financing | SRG Consulting",
    description:
      "Equipment financing strategy and lender introductions for manufacturing business owners.",
    canonical: index("/loans/manufacturing-equipment-financing"),
    ogImage: DEFAULT_OG,
  },

  // Insights articles
  "/insights/buy-sell-agreements-enterprise-value": {
    title: "Buy-Sell Agreements & Enterprise Value | SRG Consulting",
    description:
      "How buy-sell agreements protect enterprise value and what happens when an owner exits the business.",
    canonical: index("/insights/buy-sell-agreements-enterprise-value"),
    ogImage: DEFAULT_OG,
    ogType: "article",
  },
  "/insights/what-happens-when-owner-dies": {
    title: "What Happens When a Business Owner Dies | SRG Consulting",
    description:
      "The continuity, ownership, and financial consequences when a business owner dies without a funded plan.",
    canonical: index("/insights/what-happens-when-owner-dies"),
    ogImage: DEFAULT_OG,
    ogType: "article",
  },
  "/insights/commercial-loan-vs-line-of-credit": {
    title: "Commercial Loan vs. Line of Credit | SRG Consulting",
    description:
      "When a term loan beats a line of credit — and vice versa — for business owners weighing capital options.",
    canonical: index("/insights/commercial-loan-vs-line-of-credit"),
    ogImage: DEFAULT_OG,
    ogType: "article",
  },
  "/insights/key-person-risk-lending-succession": {
    title: "Key-Person Risk, Lending & Succession | SRG Consulting",
    description:
      "How key-person risk affects lending outcomes and succession readiness for closely held businesses.",
    canonical: index("/insights/key-person-risk-lending-succession"),
    ogImage: DEFAULT_OG,
    ogType: "article",
  },
  "/insights/why-banks-decline-strong-revenue": {
    title: "Why Banks Decline Strong-Revenue Businesses | SRG Consulting",
    description:
      "Common reasons banks decline profitable businesses — and how to prepare a loan review that gets approved.",
    canonical: index("/insights/why-banks-decline-strong-revenue"),
    ogImage: DEFAULT_OG,
    ogType: "article",
  },
  "/insights/executive-benefits-retain-leadership": {
    title: "Executive Benefits That Retain Leadership | SRG Consulting",
    description:
      "Bonus plans, split-dollar, and deferred compensation strategies that keep key executives through transition.",
    canonical: index("/insights/executive-benefits-retain-leadership"),
    ogImage: DEFAULT_OG,
    ogType: "article",
  },
  "/insights/business-owner-wealth-preservation": {
    title: "Business Owner Wealth Preservation | SRG Consulting",
    description:
      "How business owners preserve wealth across entity structures, trusts, and tax-aware transfer strategies.",
    canonical: index("/insights/business-owner-wealth-preservation"),
    ogImage: DEFAULT_OG,
    ogType: "article",
  },
  "/insights/prepare-commercial-loan-review": {
    title: "How to Prepare a Commercial Loan Review | SRG Consulting",
    description:
      "A practical checklist for preparing a commercial loan review that lenders approve.",
    canonical: index("/insights/prepare-commercial-loan-review"),
    ogImage: DEFAULT_OG,
    ogType: "article",
  },
  "/insights/business-succession-checklist-arizona": {
    title: "Business Succession Checklist for Arizona | SRG Consulting",
    description:
      "A step-by-step succession checklist for Arizona business owners preparing for transition.",
    canonical: index("/insights/business-succession-checklist-arizona"),
    ogImage: DEFAULT_OG,
    ogType: "article",
  },
  "/insights/trust-planning-business-continuity": {
    title: "Trust Planning for Business Continuity | SRG Consulting",
    description:
      "How trusts coordinate with entity structures and buy-sell agreements to preserve business continuity.",
    canonical: index("/insights/trust-planning-business-continuity"),
    ogImage: DEFAULT_OG,
    ogType: "article",
  },
  "/insights/buy-sell-agreement-funding-arizona": {
    title:
      "Buy-Sell Agreement Funding in Arizona: What Business Owners Must Know | SRG Consulting",
    description:
      "An unfunded buy-sell agreement is a promise without a wallet. How Arizona business owners fund buyouts with life insurance after the Connelly decision.",
    canonical: index("/insights/buy-sell-agreement-funding-arizona"),
    ogImage: DEFAULT_OG,
    ogType: "article",
  },

  // State hubs (canonical /locations/ URLs)
  "/locations/arizona": {
    title: "Business Succession & Wealth Strategy in Arizona | SRG Consulting",
    description:
      "Business succession, buy-sell funding, and wealth preservation strategy for Arizona business owners in Scottsdale, Phoenix, Mesa, Chandler, and across the state.",
    canonical: index("/locations/arizona"),
    ogImage: DEFAULT_OG,
  },
  "/locations/nevada": {
    title: "Business Succession & Wealth Strategy in Nevada | SRG Consulting",
    description:
      "Business succession, buy-sell funding, and wealth preservation strategy for Nevada business owners.",
    canonical: index("/locations/nevada"),
    ogImage: DEFAULT_OG,
  },
  "/locations/colorado": {
    title: "Business Succession & Wealth Strategy in Colorado | SRG Consulting",
    description:
      "Business succession, buy-sell funding, and wealth preservation strategy for Colorado business owners.",
    canonical: index("/locations/colorado"),
    ogImage: DEFAULT_OG,
  },
  "/locations/utah": {
    title: "Business Succession & Wealth Strategy in Utah | SRG Consulting",
    description:
      "Business succession, buy-sell funding, and wealth preservation strategy for Utah business owners.",
    canonical: index("/locations/utah"),
    ogImage: DEFAULT_OG,
  },
  "/locations/washington": {
    title:
      "Business Succession & Wealth Strategy in Washington | SRG Consulting",
    description:
      "Business succession, buy-sell funding, and wealth preservation strategy for Washington business owners.",
    canonical: index("/locations/washington"),
    ogImage: DEFAULT_OG,
  },
  "/locations/florida": {
    title: "Business Succession & Wealth Strategy in Florida | SRG Consulting",
    description:
      "Business succession, buy-sell funding, and wealth preservation strategy for Florida business owners.",
    canonical: index("/locations/florida"),
    ogImage: DEFAULT_OG,
  },
  "/locations/texas": {
    title: "Business Succession & Wealth Strategy in Texas | SRG Consulting",
    description:
      "Business succession, buy-sell funding, and wealth preservation strategy for Texas business owners.",
    canonical: index("/locations/texas"),
    ogImage: DEFAULT_OG,
  },

  // Misc
  "/brand": {
    title: "Brand Guide | SRG Consulting",
    description: "SRG Consulting PLLC brand guidelines and visual standards.",
    canonical: index("/brand"),
    robots: "noindex, follow",
  },
  "/rollover-portal": {
    title: "401(k) Rollover Portal | SRG Consulting",
    description:
      "Interactive rollover planning portal for business owners and executives.",
    canonical: index("/rollover-portal"),
    ogImage: DEFAULT_OG,
  },
};

export const defaultSEO: SEOEntry = seoConfig["/"];
